import React, { useState, useEffect } from 'react';
import { Toast } from '../../../components/common/Toast';
import MainPageLayout from '../../../components/common/MainPageLayout';
import FormCard from '../../../components/common/FormCard';
import TextEditor from './Texteditor';
import { SaveButtonIcon } from '../../../components/common/SvgIcon';
import AllShortCodes from '../../AddNewPage/components/AllShortCodes';

const CreateTemplatePage = () => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [shortcodes, setShortcodes] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchShortcodes();
  }, []);

  const fetchShortcodes = async () => {
    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/shortcodes`, {
        headers: {
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
      });

      const result = await response.json();
      if (result.success) {
        setShortcodes(result.data || {});
      }
    } catch (error) {
      console.error('Error fetching shortcodes:', error);
    }
  };

  const handleEditorChange = (newContent) => {
    setContent(newContent);
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      Toast.success('Shortcode copied!');
    } catch (err) {
      Toast.error('Failed to copy');
    }
    document.body.removeChild(textArea);
  };

  const copyShortcode = (shortcode) => {
    const placeholder = `[${shortcode}]`;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(placeholder)
        .then(() => Toast.success('Shortcode copied!'))
        .catch(() => fallbackCopy(placeholder));
    } else {
      fallbackCopy(placeholder);
    }
  };

  const handleMediaUpload = () => {
    if (typeof wp === 'undefined' || typeof wp.media === 'undefined') {
      console.error('WordPress media library is not available');
      Toast.error('Media library is not available');
      return;
    }

    const frame = wp.media({
      title: 'Select Image',
      button: { text: 'Use selected image' },
      multiple: false,
    });

    frame.on('select', () => {
      const attachment = frame.state().get('selection').first().toJSON();
      const imgHtml = `<img src="${attachment.url}" alt="${attachment.alt || attachment.title}" style="max-width:100%;">`;

      const editor = window.tinymce.get('legal-pages-template-editor');
      if (editor) editor.insertContent(imgHtml);
    });

    frame.open();
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      Toast.error('Template title cannot be empty');
      return;
    }

    if (!content.trim()) {
      Toast.error('Template content cannot be empty');
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
        body: JSON.stringify({
          name: title,
          content: content,
          type: 'free'
        }),
      });

      const result = await response.json();

      if (result.success) {
        Toast.success('Template created successfully!');
        setTimeout(() => {
          window.location.hash = '/legal-page-templates';
        }, 1500);
      } else {
        Toast.error('Failed to create template');
      }
    } catch (error) {
      console.error('Error creating template:', error);
      Toast.error('An error occurred while creating template');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    window.location.hash = '/legal-page-templates';
  };

  return (
    <MainPageLayout
      title="Create Template"
      description="Create a new legal page template"
    >
      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={handleCancel}
          className="inline-flex items-center gap-2 px-4 py-2 text-[#6F6F7B] hover:text-[#17171C] transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Templates
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Title + Editor */}
        <div className="lg:col-span-2 space-y-4">
          {/* Title Input Card */}
          <FormCard
            as="input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter template title..."
            className="!py-10 !px-6 !text-lg text-[#17171C] outline-none focus:ring-2 focus:ring-[#17171C]/5 focus:border-[#17171C]"
          />

          {/* Text Editor Card */}
          <FormCard className="mt-6 overflow-hidden">
            <div className="p-2">
              <TextEditor
                content={content}
                onChange={handleEditorChange}
                handleMediaUpload={handleMediaUpload}
                editorId="legal-pages-template-editor"
              />
            </div>
          </FormCard>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleCreate}
              disabled={saving}
              className={`
                inline-flex items-center gap-2 px-8 py-[14px] cursor-pointer rounded-xl font-semibold text-white transition shadow-[0px_4px_14px_-2px_#7C3BED40]
                ${saving ? 'bg-indigo-400 cursor-not-allowed' : 'bg-lgp-color'}
              `}
            >
              <SaveButtonIcon />
              {saving ? 'Creating...' : 'Create Template'}
            </button>

            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-6 py-[14px] border border-[#E4E4E7] text-[#6F6F7B] rounded-xl font-semibold hover:border-[#17171C] hover:text-[#17171C] transition-all disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Right Column - Shortcodes */}
        <div className="lg:col-span-1">
          <FormCard>
            <AllShortCodes
              shortcodes={shortcodes}
              copyShortcode={copyShortcode}
              loading={loading}
            />
          </FormCard>
        </div>
      </div>
    </MainPageLayout>
  );
};

export default CreateTemplatePage;